"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.hasDatabaseColumnLineage = exports.DatabaseColumnCellKeyCollisionError = exports.isDatabaseColumnType = exports.DATABASE_COLUMN_TYPES = exports.databaseColumnCellKey = void 0;
exports.databaseColumnTypeCellKey = databaseColumnTypeCellKey;
exports.planDatabaseColumnTypeChange = planDatabaseColumnTypeChange;
exports.databaseColumnDeletedKeys = databaseColumnDeletedKeys;
exports.databaseColumnCellKeys = databaseColumnCellKeys;
exports.normalizeDatabaseColumnPredecessors = normalizeDatabaseColumnPredecessors;
exports.changeDatabaseColumnType = changeDatabaseColumnType;
exports.readDatabaseCell = readDatabaseCell;
exports.readDatabaseCellAs = readDatabaseCellAs;
exports.databaseLineageColumns = databaseLineageColumns;
exports.storedDatabaseCellValue = storedDatabaseCellValue;
exports.projectDatabaseRowCells = projectDatabaseRowCells;
exports.databaseCurrentCellKeys = databaseCurrentCellKeys;
exports.databaseCellKeysOfType = databaseCellKeysOfType;
exports.deletedColumnsAfterTypeChange = deletedColumnsAfterTypeChange;
exports.databaseRowLineageWrite = databaseRowLineageWrite;
exports.databaseRowCellsAfterWrite = databaseRowCellsAfterWrite;
const database_cell_convert_1 = require("./database-cell-convert");
const database_hash_1 = require("./database-hash");
const hasOwn = Object.prototype.hasOwnProperty;
const databaseColumnCellKey = (column) => column.cellKey ?? column.id;
exports.databaseColumnCellKey = databaseColumnCellKey;
/** Every column type a database stores. */
exports.DATABASE_COLUMN_TYPES = [
    'attachment',
    'checkbox',
    'created-by',
    'created-time',
    'date',
    'doc-reference',
    'image',
    'link',
    'member',
    'multi-select',
    'number',
    'progress',
    'relation',
    'rich-text',
    'rollup',
    'select',
    'text',
    'title',
    'updated-by',
    'updated-time',
];
const columnTypes = new Set(exports.DATABASE_COLUMN_TYPES);
/** Whether `type` names a column type a database stores. */
const isDatabaseColumnType = (type) => columnTypes.has(type);
exports.isDatabaseColumnType = isDatabaseColumnType;
/** Characters of a cell key: the alphabet of generated column ids. */
const CELL_KEY_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-';
const cellKeyEncoder = new TextEncoder();
/**
 * The key a column stores its cells under once its type changed to `type`: a
 * fixed hash of the column id and the type, as long as the column id, so a
 * cell written after the change is as large as before it. Devices that make
 * the same change derive the same key. Stored rows depend on the hash: never
 * change it. A type the database does not store is refused.
 */
function databaseColumnTypeCellKey(columnId, type) {
    if (!(0, exports.isDatabaseColumnType)(type))
        throw new Error(`Unknown database column type: ${type}`);
    if (!columnId)
        throw new Error('A cell key needs a column id');
    const bytes = cellKeyEncoder.encode(`${columnId}\u0000${type}`);
    let key = '';
    // Five 6-bit characters from each 32-bit hash; seed i gives word i.
    for (let seed = 0; key.length < columnId.length; seed++) {
        const word = (0, database_hash_1.murmur3)(bytes, seed);
        for (let shift = 0; shift < 30 && key.length < columnId.length; shift += 6)
            key += CELL_KEY_ALPHABET[(word >>> shift) & 63];
    }
    return key;
}
/** A type change whose new cell key another key of the table already uses. */
class DatabaseColumnCellKeyCollisionError extends Error {
    constructor(columnId, cellKey) {
        super(`The cell key "${cellKey}" of column "${columnId}" is already used in this table.`);
        this.columnId = columnId;
        this.cellKey = cellKey;
        this.code = 'DATABASE_COLUMN_CELL_KEY_COLLISION';
        this.name = 'DatabaseColumnCellKeyCollisionError';
    }
}
exports.DatabaseColumnCellKeyCollisionError = DatabaseColumnCellKeyCollisionError;
/**
 * Plans a type change of `column` against every column of its table. A new
 * cell key that equals a column id, a current cell key or an earlier cell key
 * of the table (the column's own included) is refused, so no write happens:
 * stored cells under a key belong to exactly one column type.
 */
function planDatabaseColumnTypeChange(tableColumns, column, toType, toData) {
    const reused = toType === column.type ||
        (column.predecessors ?? []).some(predecessor => predecessor.type === toType);
    if (reused)
        return changeDatabaseColumnType(column, toType, toData, column.id);
    const cellKey = databaseColumnTypeCellKey(column.id, toType);
    for (const other of tableColumns)
        if (other.id === cellKey || databaseColumnCellKeys(other).includes(cellKey))
            throw new DatabaseColumnCellKeyCollisionError(column.id, cellKey);
    return changeDatabaseColumnType(column, toType, toData, cellKey);
}
/**
 * Every key a row may store this column's cells under, current first. A row
 * write in the current type removes the others in the same change, so a row
 * holds the column under one key.
 */
/**
 * The keys a deleted column leaves cells under: its id, its current key and
 * the keys of its earlier types. The list of deleted columns names every one,
 * so no cell of the column outlives the retention.
 */
function databaseColumnDeletedKeys(column) {
    return [...new Set([column.id, ...databaseColumnCellKeys(column)])];
}
function databaseColumnCellKeys(column) {
    return [
        (0, exports.databaseColumnCellKey)(column),
        ...(column.predecessors ?? []).map(predecessor => predecessor.cellKey),
    ];
}
/** Reads the stored defensively: peers may write anything. */
function normalizeDatabaseColumnPredecessors(value) {
    if (!Array.isArray(value))
        return [];
    const predecessors = [];
    for (const entry of value) {
        if (!entry || typeof entry !== 'object')
            continue;
        const { cellKey, type, data } = entry;
        if (typeof cellKey !== 'string' || !cellKey)
            continue;
        if (typeof type !== 'string' || !type)
            continue;
        if (predecessors.some(known => known.cellKey === cellKey))
            continue;
        predecessors.push({
            cellKey,
            type,
            data: data && typeof data === 'object' && !Array.isArray(data)
                ? data
                : {},
        });
    }
    return predecessors;
}
/**
 * Changes the type of a column in its definition only. The column keeps its
 * id; its cells move to `newCellKey`, and the current type becomes the newest
 * predecessor, so no row is written. Returning to a type the column had points
 * the column back at that type's cells and its data, so its values come back.
 * Each type appears once in the lineage, which bounds it by the number of
 * column types.
 */
function changeDatabaseColumnType(column, toType, toData, newCellKey) {
    const current = {
        cellKey: (0, exports.databaseColumnCellKey)(column),
        type: column.type,
        data: column.data,
    };
    const predecessors = column.predecessors ?? [];
    if (toType === column.type)
        return {
            type: toType,
            data: toData,
            cellKey: current.cellKey,
            predecessors,
        };
    const earlier = predecessors.find(predecessor => predecessor.type === toType);
    if (earlier)
        return {
            type: toType,
            data: earlier.data,
            cellKey: earlier.cellKey,
            predecessors: [
                current,
                ...predecessors.filter(predecessor => predecessor !== earlier),
            ],
        };
    if (databaseColumnCellKeys(column).includes(newCellKey))
        throw new Error(`Cell key "${newCellKey}" is already used by the column.`);
    return {
        type: toType,
        data: toData,
        cellKey: newCellKey,
        predecessors: [current, ...predecessors],
    };
}
function cellReader(row) {
    if (typeof row === 'function')
        return row;
    const get = row.get;
    if (typeof get === 'function' && !hasOwn.call(row, 'get'))
        return cellKey => row.get(cellKey);
    const record = row;
    return cellKey => hasOwn.call(record, cellKey) ? record[cellKey] : undefined;
}
const columnData = (data) => data && typeof data === 'object' && !Array.isArray(data)
    ? data
    : {};
/**
 * Reads the cell of `column` in `row`, the one way every reader reads a cell.
 * The value under the column's current key wins; otherwise the newest
 * predecessor with a stored value is converted into the current type.
 * Undefined when the row stores the column under no key. A column without
 * cellKey and predecessors reads the cell stored under its id.
 */
function readDatabaseCell(column, row) {
    return readDatabaseCellAs(column, column, row);
}
/**
 * Reads the cell of `column` in `row` as a column of `target` type reads it:
 * the newest stored key of the column, converted from the type stored there
 * unless it is the target type. A copy of a column reads its source this way,
 * so the copy keeps its type when the source changed type since.
 */
function readDatabaseCellAs(column, target, row) {
    const read = cellReader(row);
    const toType = target.type ?? '';
    for (const stored of [
        {
            cellKey: (0, exports.databaseColumnCellKey)(column),
            type: column.type ?? '',
            data: columnData(column.data),
        },
        ...(column.predecessors ?? []),
    ]) {
        const value = read(stored.cellKey);
        if (value === undefined)
            continue;
        if (stored.type === toType)
            return { value, cellKey: stored.cellKey, converted: false };
        return {
            value: (0, database_cell_convert_1.convertDatabaseCellValue)({
                fromType: stored.type,
                fromData: stored.data,
                toType,
                toData: columnData(target.data),
                value,
            }),
            cellKey: stored.cellKey,
            converted: true,
        };
    }
    return undefined;
}
/** Whether a column stores its cells under a key other than its id or keeps earlier types. */
const hasDatabaseColumnLineage = (column) => (column.cellKey !== undefined && column.cellKey !== column.id) ||
    (column.predecessors?.length ?? 0) > 0;
exports.hasDatabaseColumnLineage = hasDatabaseColumnLineage;
/**
 * The stored column definitions that read their cells through a lineage,
 * read defensively once per load: peers may write anything into a column.
 */
function databaseLineageColumns(columns) {
    const lineage = [];
    for (const value of columns) {
        if (!value || typeof value !== 'object')
            continue;
        const { id, type, data, cellKey, predecessors } = value;
        if (typeof id !== 'string' || !id)
            continue;
        const column = {
            id,
            data,
            ...(typeof type === 'string' ? { type } : {}),
            ...(typeof cellKey === 'string' && cellKey ? { cellKey } : {}),
            predecessors: normalizeDatabaseColumnPredecessors(predecessors),
        };
        if ((0, exports.hasDatabaseColumnLineage)(column))
            lineage.push(column);
    }
    return lineage;
}
/**
 * The value of a stored cell as the row loaders read it: the value of a
 * `{ columnId, value }` envelope, otherwise the stored entry itself.
 */
function storedDatabaseCellValue(stored) {
    return stored &&
        typeof stored === 'object' &&
        !Array.isArray(stored) &&
        'columnId' in stored &&
        'value' in stored
        ? stored.value
        : stored;
}
/**
 * Turns the cells a row stores into the cells its readers see, in place: each
 * column of `lineageColumns` reads its cell with readDatabaseCell and holds it
 * under its id; its other keys leave the view. Cells under other keys stay as
 * stored. Load boundaries call this on their own copy of a row, so every reader
 * keeps reading a cell by column id. Pass databaseLineageColumns of the
 * definition; with none, a boundary skips the call.
 */
function projectDatabaseRowCells(lineageColumns, cells, valueOf, cellOf) {
    for (const column of lineageColumns) {
        const read = readDatabaseCell(column, cellKey => hasOwn.call(cells, cellKey) ? valueOf(cells[cellKey]) : undefined);
        for (const cellKey of databaseColumnCellKeys(column))
            delete cells[cellKey];
        delete cells[column.id];
        if (read)
            cells[column.id] = cellOf(column.id, read.value);
    }
    return cells;
}
/**
 * The keys the defined columns store their current cells under. A row write
 * never removes a cell under one of them, whatever the list of deleted columns
 * says; the earlier keys of a type-changed column are not among them.
 */
function databaseCurrentCellKeys(columns) {
    const keys = [];
    for (const value of columns) {
        if (!value || typeof value !== 'object')
            continue;
        const { id, cellKey } = value;
        if (typeof id !== 'string' || !id)
            continue;
        keys.push(typeof cellKey === 'string' && cellKey ? cellKey : id);
    }
    return keys;
}
/**
 * The keys under which the defined columns store cells of `type`: the current
 * key of a column of that type and the earlier keys a column held it under. A
 * scan for files or images reads these, so a cell kept for a change back
 * still counts.
 */
function databaseCellKeysOfType(columns, type) {
    const keys = [];
    for (const value of columns) {
        if (!value || typeof value !== 'object')
            continue;
        const column = value;
        if (typeof column.id !== 'string' || !column.id)
            continue;
        if (column.type === type)
            keys.push(typeof column.cellKey === 'string' && column.cellKey
                ? column.cellKey
                : column.id);
        for (const predecessor of normalizeDatabaseColumnPredecessors(column.predecessors))
            if (predecessor.type === type)
                keys.push(predecessor.cellKey);
    }
    return keys;
}
/**
 * The list of deleted columns after a type change moved a column's cells from
 * `fromCellKey` to `toCellKey`. The cells under the old key stay for the
 * retention, so undo and a change back show them unchanged; a key the column
 * reads again leaves the list.
 */
function deletedColumnsAfterTypeChange(deleted, fromCellKey, toCellKey, now) {
    if (fromCellKey === toCellKey)
        return deleted;
    const next = { ...deleted, [fromCellKey]: now };
    delete next[toCellKey];
    return next;
}
/**
 * Plans how a row write stores the columns of `lineageColumns`, from the row
 * as stored before the write. A written column goes to its current key and
 * its earlier keys leave in the same write, so a row never holds the column
 * twice. A column the write leaves alone keeps its earlier cells until their
 * key is `removable` (its retention passed); then the write moves a converted
 * value that readers see to the current key and removes the earlier keys.
 * `stored` returns the stored value under a key, undefined when absent.
 */
function databaseRowLineageWrite(input) {
    const written = new Set(input.writtenColumnIds);
    const keys = new Map();
    const moved = {};
    const remove = [];
    for (const column of input.lineageColumns) {
        const current = (0, exports.databaseColumnCellKey)(column);
        const earlier = databaseColumnCellKeys(column).filter(cellKey => cellKey !== current);
        if (written.has(column.id)) {
            keys.set(column.id, current);
            for (const cellKey of earlier)
                if (input.stored(cellKey) !== undefined)
                    remove.push(cellKey);
            continue;
        }
        const read = readDatabaseCell(column, input.stored);
        if (read?.converted &&
            input.removable.has(read.cellKey) &&
            read.value !== null &&
            read.value !== undefined)
            moved[current] = read.value;
        for (const cellKey of earlier)
            if (input.removable.has(cellKey) && input.stored(cellKey) !== undefined)
                remove.push(cellKey);
    }
    return {
        keyOf: columnId => keys.get(columnId) ?? columnId,
        moved,
        remove,
    };
}
/**
 * A row of `{ columnId, value }` cells as a whole-row write leaves it: the
 * written cells under their column's current key, the earlier keys of written
 * columns and every removable key gone, and a converted value whose earlier
 * key leaves moved to its column's current key. `written` holds cells by
 * column id; `stored` and the result hold them by cell key.
 */
function databaseRowCellsAfterWrite(input) {
    const { stored, written } = input;
    const lineage = databaseRowLineageWrite({
        lineageColumns: input.lineageColumns,
        removable: input.removable,
        writtenColumnIds: Object.keys(written),
        stored: cellKey => hasOwn.call(stored, cellKey) ? stored[cellKey].value : undefined,
    });
    const gone = new Set([...input.removable, ...lineage.remove]);
    const cells = {};
    for (const [cellKey, cell] of Object.entries(stored))
        if (!gone.has(cellKey))
            cells[cellKey] = cell;
    for (const [cellKey, value] of Object.entries(lineage.moved))
        cells[cellKey] = { columnId: cellKey, value };
    for (const [columnId, cell] of Object.entries(written)) {
        const cellKey = lineage.keyOf(columnId);
        cells[cellKey] =
            cellKey === columnId ? cell : { ...cell, columnId: cellKey };
    }
    return cells;
}
